/// <amd-module name="Wiki/CreateWit/CreateWitUtils" />
import { MarkdownConstants } from "VSS/Features/Markdown/MarkdownConstants";
import { LineAttrInfo, LineAttrInfoMap } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { SelectionElements } from "Wiki/CreateWit/SelectionElements";

const enum HTMLNodeType {
    NodeType_Text = 3
}

const enum SelectionContainerPosition {
    START = 1,
    END = 2
}

export const enum ClassNamesConstants {
    WitMenuItems = "wit-menu-items",
    WikiMdContainer = "wiki-md-container",
    WitMenuContainer = "wit-menu-container",
    WitMenuRefContainer = "wit-menu-ref-element",
    UserMention = "mention-preview-person",
    WITMention = "mention-widget-workitem",
    PRMention = "mention-widget-item"
}

const RegexSpecialCharsRegex = "[[{()*+?.\\^$|]";

/**
 * Check whether the selected text is in link/mentions/code/Yaml blocks.
 */
export function isSelectionAllowed(range: Range): boolean {
    let parentElement = _isNodeOfTextType(range.endContainer) ? _getParentElement(range.endContainer) : _getParentElement(range.startContainer);
    while (parentElement !== null && !parentElement.classList.contains(ClassNamesConstants.WikiMdContainer)) {
        if (
            parentElement.nodeName === "A" ||
            parentElement.nodeName === "CODE" ||
            parentElement.classList.contains("metadata-yaml-table") ||
            parentElement.classList.contains("toc-container") ||
            parentElement.classList.contains("wiki-mermaid-container")
        ) {
            return false;
        }
        parentElement = _getParentElement(parentElement);
    }
    return true;
}

/**
 * Return true if given node has parent with given className.
 */
export function hasParentNode(node: HTMLElement | null, className: string): boolean {
    while (node) {
        if (node.classList && node.classList.contains(className)) {
            break;
        }
        node = _getParentElement(node);
    }
    return !!node;
}

export function shouldShowCreateWitMenu(range: Range): boolean {
    const isStartWithinMdContainer = hasParentNode(range.startContainer as HTMLElement, ClassNamesConstants.WikiMdContainer);
    let isEndWithinMdContainer = true;
    // Check for end container only if its of text type node. Selection using double click on last line,
    // end container goes beyond WikiMdContainer, so checking for end container only if its of text type node.
    if (_isNodeOfTextType(range.endContainer)) {
        isEndWithinMdContainer = hasParentNode(range.endContainer as HTMLElement, ClassNamesConstants.WikiMdContainer);
    }
    return isStartWithinMdContainer && isEndWithinMdContainer;
}

/**
 * Update the page content by embedding the created work-item by replacing the selected text.
 * Returns content to update if embedded successfully else undefined if not able to.
 */
export function embedWorkItemToTheSelectedPlace(
    range: Range,
    wikiElements: SelectionElements,
    workItemId: number,
    markdownContent: string,
    markdownLineMapping: LineAttrInfoMap
): string | undefined {
    const startContainerSelectedText = _getSelectedText(range, SelectionContainerPosition.START);
    let endContainerSelectedText;
    let selectionIsInSameContainer = false;
    const isEndContainerOfTextType = _isNodeOfTextType(range.endContainer);
    if (range.startContainer === range.endContainer) {
        selectionIsInSameContainer = true;
        endContainerSelectedText = startContainerSelectedText;
    } else if (!isEndContainerOfTextType) {
        endContainerSelectedText = _trimFirstNewLineChar(wikiElements.lastTextNode.nodeValue);
    } else {
        endContainerSelectedText = _getSelectedText(range, SelectionContainerPosition.END);
    }
    // Ideally texts should not be empty. Its more of a seat belt so that
    // text matching in the next step using regex doesn't go infinite.
    if (!startContainerSelectedText || !endContainerSelectedText) {
        return undefined;
    }
    // TODO: Do we need trim selectedText? if we trim we need to handle
    // corner scenario where only spaces comes in the end container.
    const lines: (string | undefined)[] = markdownContent.split("\n");

    // Variable name with suffix Start, corresponds to the startContainerSelectedText
    // and with suffix End, corresponds to the endContainerSelectedText
    const matchesWithStart: [number, LineAttrInfo | null] = _getTextMatchCountAndLineNo(
        range,
        range.startContainer as HTMLElement,
        startContainerSelectedText,
        markdownLineMapping
    );
    // Find occurrences with start selection.
    const matchedLinesStart = matchesWithStart[1] || { startLine: 0, endLine: lines.length };
    const pageSectionToBeSearchedOnForStart = lines.slice(matchedLinesStart.startLine, matchedLinesStart.endLine).join("\n");
    const startIndexStart = _getIndexWithOccurrenceCount(startContainerSelectedText, pageSectionToBeSearchedOnForStart, matchesWithStart[0]);

    // Find occurrences with end selection
    let matchedLinesEnd;
    let startIndexEnd;
    let pageSectionToBeSearchedOnForEnd;
    let matchesWithEnd: [number, LineAttrInfo | null];
    if (!selectionIsInSameContainer) {
        matchesWithEnd = _getTextMatchCountAndLineNo(range, wikiElements.lastTextNode as HTMLElement, endContainerSelectedText, markdownLineMapping);
        matchedLinesEnd = matchesWithEnd[1] || { startLine: 0, endLine: lines.length };
        pageSectionToBeSearchedOnForEnd = lines.slice(matchedLinesEnd.startLine, matchedLinesEnd.endLine).join("\n");
        startIndexEnd = _getIndexWithOccurrenceCount(endContainerSelectedText, pageSectionToBeSearchedOnForEnd, matchesWithEnd[0]);
    } else {
        // Just assign the ones from start selection related props, as both are same.
        matchesWithEnd = matchesWithStart;
        matchedLinesEnd = matchedLinesStart;
        pageSectionToBeSearchedOnForEnd = pageSectionToBeSearchedOnForStart;
        startIndexEnd = startIndexStart;
    }

    // Embed work item by replacing the selected text
    if (startIndexStart !== -1 && startIndexEnd !== -1) {
        const endIndexOfEndSelection = startIndexEnd + endContainerSelectedText.length;
        let pageSectionBeforeSelection = pageSectionToBeSearchedOnForStart.substr(0, startIndexStart);
        let pageSectionAfterSelection = pageSectionToBeSearchedOnForEnd.substr(endIndexOfEndSelection);

        // Sanitize the markups in the selection. Handling only for single
        // level markups like (**hello**), not the nested ones like (_**hello**_)
        // Like for text ( Test **Hello** world ),if  selection is Test Hello,
        // then output should be (world) only. so need to remove the ** before world.
        // There are lot of such combinations for which we need to sanitize.
        const startContainerMarkup = _getNodeMarkupValue(range.startContainer, markdownLineMapping);
        if (startContainerMarkup !== "") {
            // Removing markup if endswith markup, because if it ends with **, means the selection starts from starting char of the bold text.
            // In this case remove the last ** because replacing immediate next would break the syntax.
            // Consider the text `Hello **how are you** dude`, if selection is `how` or `how are you` or `how are you dude`,
            // pageSectionBeforeSelection would be `Hello **` so trimming last ** within if statement.
            // if the selection is, `are` or `are you dude`, pageSectionBeforeSelection would be `Hello **how`, so in this case just appending ** to complete the syntax as next thing is gonna replaced.
            // Now what to do with the end selection is handled similarly in next if block.
            // (end selection can end on text node or other type of markup node, with ending on start, mid or end chars.
            const endsWithMarkup = pageSectionBeforeSelection.endsWith(startContainerMarkup);
            if (endsWithMarkup) {
                pageSectionBeforeSelection = pageSectionBeforeSelection.substr(0, pageSectionBeforeSelection.length - startContainerMarkup.length);
            } else {
                pageSectionBeforeSelection += startContainerMarkup;
            }
        }
        const endContainerMarkup = _getNodeMarkupValue(
            isEndContainerOfTextType ? range.endContainer : wikiElements.lastTextNode,
            markdownLineMapping
        );
        if (endContainerMarkup !== "") {
            const startsWithMarkup = pageSectionAfterSelection.startsWith(endContainerMarkup);
            if (startsWithMarkup) {
                pageSectionAfterSelection = pageSectionAfterSelection.substr(endContainerMarkup.length);
            } else {
                pageSectionAfterSelection = endContainerMarkup + pageSectionAfterSelection;
            }
        }
        const fullContent =
            lines.slice(0, matchedLinesStart.startLine).join("\n") +
            (matchedLinesStart.startLine > 0 ? "\n" : "") +
            pageSectionBeforeSelection +
            " #" +
            workItemId +
            " " +
            pageSectionAfterSelection +
            (matchedLinesEnd.endLine < lines.length ? "\n" : "") +
            lines.slice(matchedLinesEnd.endLine).join("\n");
        return fullContent;
    }

    return undefined;
}

/**
 * It inspects the text selection (by checking range param) and populates following.
 * 1. mentions or HTML tags with their occurrences
 * 2. Last node which is of type text.
 */
export function getSelectionElements(range: Range, selectedText: string): SelectionElements {
    const wikiElements = new SelectionElements();
    const tempSelectionElements = new SelectionElements();
    let node: HTMLElement | null = range.startContainer as HTMLElement;
    let prevNode = node;
    const endNode = range.endContainer as HTMLElement;
    let isSameNode = range.endContainer === range.startContainer;
    const selectedTextCount = _countOfChar(selectedText);
    wikiElements.exploredTextCharCount = _countOfChar(_getSelectedText(range, SelectionContainerPosition.START));
    wikiElements.lastTextNode = node;
    while (node && !isSameNode) {
        prevNode = node;
        // Check all nodes inside the next sibling
        node = node.nextSibling as HTMLElement;
        if (node) {
            isSameNode = _inspectNode(node, true, endNode, wikiElements, tempSelectionElements, selectedTextCount);
        } else {
            node = _getParentElement(prevNode);
            if (node && node.classList && node.classList.contains(ClassNamesConstants.WikiMdContainer)) {
                break;
            }
            // In case of parent node, just inspect the node and not its
            // children because they would have already been inspected by now
            // by checking the nextSibling of the previous node.
            isSameNode = _inspectNode(node, false, endNode, wikiElements, tempSelectionElements, selectedTextCount);
        }
    }
    return wikiElements;
}

/**
 * Escape < and > html chars
 */
export function escapeHTMLChars(text: string): string {
    const escapedText = text.replace(/</g, "&lt;");
    return escapedText.replace(/>/g, "&gt;");
}

function _getNodeMarkupValue(node: Node | null, markdownLineMapping: LineAttrInfoMap): string {
    let markup = "";
    const parentNode: HTMLElement | null = _getParentElement(node);
    if (parentNode) {
        const mappingAttrValue = parentNode.getAttribute(MarkdownConstants.MarkDownLinesMappingAttrName);
        if (mappingAttrValue && !markdownLineMapping[mappingAttrValue].isBlock) {
            markup = markdownLineMapping[mappingAttrValue].markup;
        }
    }
    return markup;
}

function _trimFirstNewLineChar(str: string | null): string {
    if (str === null) {
        return "";
    }
    if (str.charAt(0) === `\n`) {
        return str.substring(1);
    }
    return str;
}

function _getIndexWithOccurrenceCount(searchText: string, searchOnText: string, numOfOccurrence: number): number {
    let count = 0;
    let index = -1;
    let match: RegExpExecArray | null;
    const pattern = new RegExp(_escapeRegSpecialChars(searchText), "g");
    while ((match = pattern.exec(searchOnText))) {
        count++;
        if (count === numOfOccurrence) {
            index = match.index;
            break;
        }
    }
    return index;
}

/**
 * Return the count of chars exclusing spaces, tabs and new line.
 * We are excluding
 * 1. spaces or tabs because, function window.getSelection().toString() removes multiple spaces in between, but
 *    when inspecting the HTMLElements nodeValue (the text), it has all the multiple spaces.
 * 2. new line because, we don't want to consider last text node selection which is line break.
 */
function _countOfChar(text: string): number {
    let count = 0;
    for (let i = 0; i < text.length; i++) {
        const char = text.charAt(i);
        if (char !== " " && char !== "\t" && char !== "\n") {
            count++;
        }
    }
    return count;
}

/**
 * This function recursively checks node and its children for the
 * HTML tags and keeps the count of them in the wikiElements param.
 * @param node node to inspec
 * @param inspectChildren boolean flag indicating whether to recursively inspect node children
 * @param endNode node till which we need to inspect.
 * @param wikiElements storage to store count of all the html tags, mentions and witTables explored while inspecting.
 * @param tempSelectionElements temp storage to store count of all the html tags, mentions and witTables explored while inspecting.
 * This is merged with the `wikiElements` whenever we encounter node of text type. This is to avoid adding the html tags
 * which are not actually selected (visually), but because of the behavior of selection api, end container of selection goes to the
 * next html block of text selection. (usually happens when selecting text block using double click or extending the selection beyond the
 * line using mouse)
 * @param maxCharsToInspect count of the chars to explore while inspecting
 *
 * Returns true, if
 * 1. The node or any of its children is same as endNode param while inspecting nodes recursively.
 * 2. Total number of chars explored while ispecting is greater than or equal to `maxCharsToInspect`.
.
 */
function _inspectNode(
    node: HTMLElement | null,
    inspectChildren: boolean,
    endNode: HTMLElement,
    wikiElements: SelectionElements,
    tempSelectionElements: SelectionElements,
    maxCharsToInspect: number
): boolean {
    if (node) {
        let mentionOrWitTableFound = false;
        if (node.classList) {
            mentionOrWitTableFound = true;
            if (node.classList.contains(ClassNamesConstants.UserMention)) {
                tempSelectionElements.peopleMentions++;
            } else if (_isWitTableBlock(node)) {
                tempSelectionElements.witTables++;
            } else if (node.classList.contains(ClassNamesConstants.WITMention)) {
                tempSelectionElements.witMentions++;
            } else if (node.classList.contains(ClassNamesConstants.PRMention)) {
                tempSelectionElements.prMentions++;
            } else {
                mentionOrWitTableFound = false;
            }
        }
        // If no mentions or wit tables were found, add the HTML tag.
        // because we don't want links to be counted in these cases.
        // Excluding text type node as they are not the actual HTML tags.
        if (!mentionOrWitTableFound && !_isNodeOfTextType(node)) {
            const count = tempSelectionElements.htmlTags[node.nodeName];
            tempSelectionElements.htmlTags[node.nodeName] = count ? count + 1 : 1;
        }
        // If text type node is found, merge the temp elements to the wikiElements.
        // (This is to avoid taking into consideration of those nodes which are not
        // in the selection (visually). In case of selection using the double click
        // or stretching the selection beyond the line, the end container is returned
        // as the html node of next html block, which we don't want to consider while
        // inspecting because it will unnecessarily give html tags of next html block.

        // Keep maintaining the count of the chars explored so that if it reaches
        // the selected chars count, we can stop inspecting further.
        // Avoiding text node with only new line, to avoid line break nodes.
        if (_isNodeOfTextType(node) && node.nodeValue !== "\n") {
            // merge and reset the temp object
            wikiElements.merge(tempSelectionElements);
            tempSelectionElements.reset();
            if (node.nodeValue) {
                wikiElements.exploredTextCharCount += _countOfChar(node.nodeValue);
                wikiElements.lastTextNode = node;
            }
        }
        // If the end node is found, return true.
        if (node.isSameNode(endNode) || wikiElements.exploredTextCharCount >= maxCharsToInspect) {
            return true;
        }
    }
    if (inspectChildren && node && node.hasChildNodes && node.hasChildNodes()) {
        // Recursively check for matching on the child node
        // not using forEach to loop because forEach on object is not supported on IE.
        for (let i = 0; i < node.childNodes.length; i++) {
            const nodeIsEndNode = _inspectNode(
                node.childNodes[i] as HTMLElement,
                true,
                endNode,
                wikiElements,
                tempSelectionElements,
                maxCharsToInspect
            );
            if (nodeIsEndNode) {
                return true;
            }
        }
    }
    return false;
}

/**
 * Escaping the regex speical characters
 *
 */
function _escapeRegSpecialChars(text: string): string {
    const pattern = new RegExp(RegexSpecialCharsRegex, "g");
    return text.replace(pattern, "\\$&");
}

function _getParentElement(node: Node | null): HTMLElement | null {
    let parentElement: HTMLElement | null = null;
    if (node && node.parentNode instanceof Element) {
        parentElement = node.parentNode as HTMLElement;
    }
    return parentElement;
}

/**
 * Returns the number of occurrence 'textToSearch' in the 'str' string
 */
function _getCountOfString(str: string | null, textToSearch: string): number {
    if (!str || textToSearch === "") {
        return 0;
    }
    const pattern = new RegExp(_escapeRegSpecialChars(textToSearch), "g");
    let match: RegExpExecArray | null;
    let count = 0;
    while ((match = pattern.exec(str))) {
        count++;
    }
    return count;
}

/**
 * Checking the class name only to check if the node in a mention block
 */
function _isMentionBlock(node: HTMLElement | null): boolean {
    if (node && node.classList) {
        if (
            node.classList.contains(ClassNamesConstants.UserMention) || // people
            node.classList.contains(ClassNamesConstants.WITMention) || // work item
            node.classList.contains(ClassNamesConstants.PRMention) // pr
        ) {
            return true;
        }
    }
    return false;
}

function _isWitTableBlock(node: HTMLElement | null): boolean {
    if (node && node.classList) {
        return node.classList.contains("wiki-wit-table-container");
    }
    return false;
}

/**
 * Returns the count of occurrence of text to be matched inside the node.
 * It recursively checks all the child nodes of the given node
 *
 * Avoid checking the nodes that is for mentions or wit-query, because
 * content under these node won't be found in the markdown.
 */
function _getTextMatchCountInsideNode(node: HTMLElement | null, textToMatch: string): number {
    let count = 0;
    if (node && !_isMentionBlock(node) && !_isWitTableBlock(node)) {
        if (_isNodeOfTextType(node)) {
            return _getCountOfString(node.textContent, textToMatch);
        } else if (node.hasChildNodes()) {
            // not using forEach to loop because forEach on object is not supported on IE.
            for (let i = 0; i < node.childNodes.length; i++) {
                if (_isNodeOfTextType(node.childNodes[i])) {
                    // for text type node, if text matches increment the count
                    count += _getCountOfString(node.childNodes[i].textContent, textToMatch);
                } else {
                    // Recursively check for matching on the child node
                    count += _getTextMatchCountInsideNode(node.childNodes[i] as HTMLElement, textToMatch);
                }
            }
        }
    }
    return count;
}

function _isNodeOfTextType(node: HTMLElement | ChildNode | Node): boolean {
    return node.nodeType === HTMLNodeType.NodeType_Text;
}

/**
 * Returns the total number of occurrences of text to match and
 * the line numbers of markdown text within which its searched.
 *
 * It finds out the text before the selection in the endContainer
 * so that same number of occurrances can be matched from markdown content.
 */
function _getTextMatchCountAndLineNo(
    range: Range,
    node: HTMLElement,
    textToMatch: string,
    markdownLineMapping: LineAttrInfoMap
): [number, LineAttrInfo | null] {
    // TODO:
    // 1. Handle spaces only selected in the end container.
    let count = 0;
    if (range.endContainer === range.startContainer) {
        const textBeforeSelection = (node.textContent || "").substr(0, range.endOffset);
        count = _getCountOfString(textBeforeSelection, textToMatch);
    } else {
        // if both are not in the same container, the end container text must be there in the markdown.
        // (as selection API only gives the text nodes only as the start and end container)
        count = 1;
    }

    let prevNode = node;
    do {
        prevNode = node;
        node = node.previousSibling as HTMLElement;
        // Check all nodes inside the previous sibling
        count += _getTextMatchCountInsideNode(node, textToMatch);
        if (!node) {
            node = prevNode.parentNode as HTMLElement;
        }
    } while (
        _isNodeOfTextType(node) ||
        (typeof node.className === "string" &&
        node.className.search(ClassNamesConstants.WikiMdContainer) === -1 && // node is not the wiki md container
            !_isMarkdownBlock(node, markdownLineMapping)) // Only block level attr, because in markdown rendering, block level starts with new line.
        // Don't want to consider inline syntax like bold or italics
    );
    let lineIndexes = null;
    const mappingAttrValue = node.getAttribute(MarkdownConstants.MarkDownLinesMappingAttrName);
    if (mappingAttrValue) {
        lineIndexes = markdownLineMapping[mappingAttrValue];
    }

    return [count, lineIndexes];
}

function _isMarkdownBlock(node: HTMLElement, markdownLineMapping: LineAttrInfoMap): boolean {
    const mappingAttrValue = node.getAttribute(MarkdownConstants.MarkDownLinesMappingAttrName);
    if (mappingAttrValue) {
        return markdownLineMapping[mappingAttrValue] && markdownLineMapping[mappingAttrValue].isBlock;
    }
    return false;
}

/**
 * Retuns the selected text on the end container
 * For ex. for selection of "Hello world" of html <span>Hello <b>world</b></span>
 * it returns "world" as selected text.
 */
function _getSelectedText(range: Range, containerPos: SelectionContainerPosition): string {
    const containerText = containerPos === SelectionContainerPosition.START ? range.startContainer.textContent : range.endContainer.textContent;
    if (!containerText) {
        return "";
    }

    // Check if section is within the same container
    let selectionStartOffset = 0;
    let selectionEndOffset = 0;
    if (range.endContainer === range.startContainer) {
        selectionStartOffset = range.startOffset;
        selectionEndOffset = range.endOffset;
    } else {
        selectionEndOffset = containerText.length;
    }
    let selectedTextOnContainer =
        containerPos === SelectionContainerPosition.START
            ? containerText.substring(range.startOffset, selectionEndOffset)
            : containerText.substring(selectionStartOffset, range.endOffset);

    // Remove the preceeding newline char, because we don't want to replace text with newline.
    if (selectedTextOnContainer.charAt(0) === `\n`) {
        selectedTextOnContainer = selectedTextOnContainer.substring(1);
    }
    return selectedTextOnContainer;
}
